import { ERROR_CODES } from '@garage/shared';
import { Link, Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useLogout } from '../garage/lib/session.js';
import { PlatformLogo } from '../public/PlatformMark.js';
import { usePlatformIdentity } from '../public/platform.js';
import { useBrand, useMe } from '../shared/auth.js';
import { AccountMenu } from '../shared/components/AccountMenu.js';
import { clearSessionIssue, useSessionIssue } from '../shared/session-issues.js';
import { Button, buttonClasses } from '../ui/Button.js';
import { cx } from '../ui/cx.js';
import { BuildingIcon, GridIcon, LifebuoyIcon, LogOutIcon } from '../ui/icons.js';
import type { MenuItem } from '../ui/Menu.js';
import { Alert, SkeletonList } from '../ui/States.js';

/** Largeur de la console : pensée pour l'ordinateur, utilisable sur téléphone. */
export const ADMIN_CONTAINER = 'mx-auto w-full max-w-[1120px] px-[var(--page-x)]';

/** Onglets sur le bandeau anthracite : blanc atténué, onglet courant souligné de rouge (forme + couleur). */
const NAV_LINK =
  'inline-flex min-h-(--ctl-sm) items-center gap-2 rounded-lg px-3 text-sm font-medium text-on-platform-2 transition-colors hover:bg-platform-panel-raised hover:text-on-platform focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-on-platform aria-[current=page]:bg-platform-panel-raised aria-[current=page]:text-on-platform aria-[current=page]:shadow-[inset_0_-3px_0_var(--platform-accent-on-panel)]';

/**
 * Console plateforme `/admin` (vague 5) : réservée aux administrateurs plateforme. L'API vérifie le
 * rôle sur chaque route (`requirePlatformAdmin`) ; l'interface ne fait qu'orienter.
 * Présentation neutre (aucune marque de garage).
 */
export default function AdminLayout() {
  const me = useMe();
  const { name: productName, suffix } = usePlatformIdentity();
  useBrand(null, productName ? `Console plateforme · ${productName}` : 'Console plateforme');
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const logout = useLogout();
  const navigate = useNavigate();
  const issue = useSessionIssue();

  if (me.isPending) {
    return (
      <main className={cx(ADMIN_CONTAINER, 'py-10')}>
        <SkeletonList rows={3} />
      </main>
    );
  }
  if (!me.data) return <Navigate to="/login" replace />;
  if (!me.data.isPlatformAdmin) {
    return (
      <main className="mx-auto max-w-md px-[var(--page-x)] py-16">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">Accès réservé</h1>
        <p className="mt-2 text-ink-2">Cette console est réservée à l’administration de la plateforme.</p>
        <Link to="/" className={buttonClasses({ variant: 'primary', size: 'lg', block: true, className: 'mt-6' })}>
          Revenir à l’accueil
        </Link>
      </main>
    );
  }
  const onSupport = pathname.startsWith('/admin/support');
  // Vague 7 : démonstrations, présentées à part des garages clients.
  const onDemos = pathname.startsWith('/admin/demos');
  const { user, memberships } = me.data;
  const menuItems: MenuItem[] = [];
  if (memberships.length > 0) {
    menuItems.push({ key: 'garages', label: 'Mes garages', icon: <BuildingIcon size={18} />, onSelect: () => void navigate({ to: '/choose-garage' }) });
  }
  menuItems.push({ key: 'logout', label: 'Se déconnecter', icon: <LogOutIcon size={18} />, onSelect: () => void logout() });

  return (
    <div className="platform-scope min-h-dvh bg-canvas">
      {/* Bandeau de la plateforme (direction « Rouge Pulse ») : anthracite, filet rouge, logo blanc. */}
      <header className="platform-panel border-b-[3px] border-platform-accent-on-panel [--on-masthead:var(--platform-on-panel)]">
        <div className={cx(ADMIN_CONTAINER, 'flex flex-wrap items-center gap-x-6 gap-y-2 py-3')}>
          <div className="flex items-end gap-3">
            <PlatformLogo name={productName} suffix={suffix} tone="dark" width={132} />
            <p className="text-sm font-medium text-on-platform-2">Console plateforme</p>
          </div>
          <nav aria-label="Console" className="-mx-1 flex max-w-full gap-1 overflow-x-auto">
            <Link to="/admin" className={NAV_LINK} aria-current={onSupport || onDemos ? undefined : 'page'}>
              <BuildingIcon size={18} />
              Garages
            </Link>
            <Link to="/admin/demos" className={NAV_LINK} aria-current={onDemos ? 'page' : undefined}>
              <GridIcon size={18} />
              Démonstrations
            </Link>
            <Link to="/admin/support" className={NAV_LINK} aria-current={onSupport ? 'page' : undefined}>
              <LifebuoyIcon size={18} />
              Accès support
            </Link>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden max-w-48 truncate text-sm text-on-platform-2 sm:block">{user.name}</span>
            <AccountMenu variant="masthead" name={user.name} email={user.email} detail="Administration de la plateforme" items={menuItems} />
          </div>
        </div>
      </header>
      <main className={cx(ADMIN_CONTAINER, 'py-6 lg:py-10')}>
        {issue?.code === ERROR_CODES.supportAccessEnded ? (
          <Alert
            tone="warning"
            title="L’accès support a pris fin"
            className="mb-6"
            action={
              <Button variant="secondary" size="sm" onClick={clearSessionIssue}>
                Masquer
              </Button>
            }
          >
            {issue.message}
          </Alert>
        ) : null}
        <Suspense fallback={<SkeletonList rows={3} />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
